import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAPI, formatFullName } from "../lib/utils";
import { matchesCoachAthleteSearch } from "../lib/coachUtils";
import { SportsExplainer } from "./sports/SportsExplainer";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogTrigger,
} from "./ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "./ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
	Building2,
	Users,
	Plus,
	Trash2,
	X,
	Search,
	UserCog,
	BarChart3,
	Clock,
	Activity,
	ChevronDown,
	ChevronUp,
	Shield,
	UserPlus,
	UserMinus,
	ExternalLink,
	Share2,
	Bot,
	Settings2,
	Check,
} from "lucide-react";
import { toast } from "sonner";

const DISCIPLINE_SUMMARY_TONES = {
	volt: "border-volt/50 bg-volt/10 text-volt",
	blaze: "border-blaze/50 bg-blaze/10 text-blaze",
};

// Compact read-only recap of a coach's disciplines: the first few labels plus a
// "+N" overflow chip. The full editable grids live in the permissions dialog,
// so the card stays scannable.
function DisciplineSummary({ label, values, sports, emptyText, tone }) {
	const selected = Array.isArray(values) ? values : [];
	const labels = selected.map(
		(value) => sports.find((s) => s.value === value)?.label || value,
	);
	const shown = labels.slice(0, 2);
	const extra = labels.length - shown.length;
	return (
		<div className="flex items-start gap-1.5 text-xs">
			<span className="text-muted-foreground/70 shrink-0">{label}:</span>
			{labels.length === 0 ? (
				<span className="text-muted-foreground/60 italic">{emptyText}</span>
			) : (
				<span className="flex flex-wrap items-center gap-1 min-w-0">
					{shown.map((l) => (
						<span
							key={l}
							className={`rounded-sm border px-1.5 py-px text-[10px] ${DISCIPLINE_SUMMARY_TONES[tone]}`}
						>
							{l}
						</span>
					))}
					{extra > 0 && (
						<span className="text-[10px] text-muted-foreground">+{extra}</span>
					)}
				</span>
			)}
		</div>
	);
}

// The platform-wide picker in the coach athletes dialog lists search results
// only, capped: 250+ rows nobody scrolls were half of what made it unreadable.
const ADD_RESULTS_LIMIT = 30;

function sortByName(athletes) {
	return [...athletes].sort((a, b) =>
		(a.name || "").localeCompare(b.name || "", undefined, {
			sensitivity: "base",
		}),
	);
}

// Groups the athletes a coach reaches through teams and shared groups by where
// they come from, so the dialog reads "group X brings 12 athletes" instead of a
// flat list with a chip on every row. An athlete in two groups shows up under
// both; the team bucket only collects those no shared group explains (the team
// written on the athlete, usually by an invite link). Group and team names are
// user data and stay untranslated.
function buildSharedAthleteBuckets(athletes, t) {
	const buckets = new Map();
	const add = (key, bucket, athlete) => {
		if (!buckets.has(key)) buckets.set(key, { key, ...bucket, athletes: [] });
		buckets.get(key).athletes.push(athlete);
	};
	for (const athlete of athletes) {
		const groups = athlete.via_groups || [];
		for (const group of groups) {
			const meta = [
				group.owner_name &&
					t("adminCoachAthletesGroupOwner").replace("{name}", group.owner_name),
				group.team_names?.length &&
					`${t("adminCoachAthletesViaTeam")} ${group.team_names.join(", ")}`,
			]
				.filter(Boolean)
				.join(" · ");
			add(
				`group:${group.group_id}`,
				{ kind: "group", label: group.name, meta },
				athlete,
			);
		}
		if (groups.length > 0) continue;
		for (const team of athlete.via_teams || []) {
			add(
				`team:${team.team_id}`,
				{
					kind: "team",
					label: `${t("adminCoachAthletesViaTeam")} ${team.name}`,
					meta: t("adminCoachAthletesTeamBucketMeta"),
				},
				athlete,
			);
		}
	}
	return [...buckets.values()]
		.map((bucket) => ({ ...bucket, athletes: sortByName(bucket.athletes) }))
		.sort((a, b) => b.athletes.length - a.athletes.length);
}

function CoachAthleteRow({ athlete, action, muted }) {
	return (
		<div
			className={`flex items-center gap-2.5 px-2 py-1.5 rounded-sm ${
				muted ? "bg-secondary/50" : "bg-secondary"
			}`}
		>
			<Avatar className="w-7 h-7">
				<AvatarImage src={athlete.picture} />
				<AvatarFallback
					className={`text-xs ${
						muted ? "bg-muted text-muted-foreground" : "bg-volt/20 text-volt"
					}`}
				>
					{athlete.name?.charAt(0).toUpperCase()}
				</AvatarFallback>
			</Avatar>
			<div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
				<p className="text-sm truncate">{athlete.name}</p>
				<p className="text-[11px] text-muted-foreground truncate">
					{athlete.email}
				</p>
			</div>
			{action}
		</div>
	);
}

export function CoachManagement({ onDataChanged }) {
	const { language, t } = useLanguage();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [coachDetails, setCoachDetails] = useState([]);
	const [teams, setTeams] = useState([]);
	const [allCoaches, setAllCoaches] = useState([]);
	const [coachAssignments, setCoachAssignments] = useState({
		coaches: [],
		teams: [],
		unassigned_athletes: [],
	});

	// Coach Teams state
	const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
	const [newTeam, setNewTeam] = useState({ name: "", description: "" });
	const [teamCoachSelections, setTeamCoachSelections] = useState({});
	const [teamsOpen, setTeamsOpen] = useState(true);

	// Coach list state
	const [searchQuery, setSearchQuery] = useState("");

	// Detail modal state
	const [detailModal, setDetailModal] = useState(null); // { type: 'permissions'|'athletes'|'programs'|'teams', coach }
	const [allAthletes, setAllAthletes] = useState([]);
	const [modalSearch, setModalSearch] = useState("");
	const [athletesTab, setAthletesTab] = useState("direct");

	// Disciplines a coach can be assigned to. They drive the practice domains
	// (physical / shooting) and therefore which surfaces the coach is shown.
	// See docs/shooting/07-domini-e-abilitazioni.md
	const [sports, setSports] = useState([]);
	const [savingDisciplinesFor, setSavingDisciplinesFor] = useState(null);

	// Gli sport da proporre per un coach: i globali attivi e i suoi personali,
	// più quelli che ha già — disattivati, o personali di un altro coach — che
	// si possono togliere ma non riproporre. Gli sport personali restano nel
	// perimetro di chi li ha creati (docs/sport-config, D1): si allargano
	// promuovendoli dal tab Discipline, non assegnandoli coach per coach.
	const sportsFor = (coach, selected) =>
		sports.filter((sport) => {
			if ((selected || []).includes(sport.value)) return true;
			if (sport.is_active === false) return false;
			return (
				sport.library_scope !== "personal" ||
				sport.owner?.user_id === coach?.user_id
			);
		});

	const loadData = useCallback(async () => {
		try {
			const [detailsData, teamsData, assignmentsData, sportsData] =
				await Promise.all([
					fetchAPI("/admin/coach-details").catch(() => ({ coaches: [] })),
					fetchAPI("/teams").catch(() => []),
					fetchAPI("/admin/coaches-with-athletes").catch(() => ({
						coaches: [],
						teams: [],
						unassigned_athletes: [],
					})),
					// Il catalogo completo: le etichette di ogni sport, anche
					// disattivato o personale di un coach (docs/sport-config, Fase 3).
					fetchAPI("/admin/sports").catch(() => []),
				]);
			setCoachDetails(detailsData.coaches || []);
			setAllAthletes(detailsData.all_athletes || []);
			setTeams(Array.isArray(teamsData) ? teamsData : []);
			setAllCoaches(
				(detailsData.coaches || []).map((c) => ({
					user_id: c.user_id,
					name: c.name,
					email: c.email,
				})),
			);
			setCoachAssignments(assignmentsData);
			setSports(Array.isArray(sportsData) ? sportsData : []);
		} catch (error) {
			toast.error("Errore nel caricamento dati coach");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const reload = () => {
		loadData();
		if (onDataChanged) onDataChanged();
	};

	const formatLastAccess = (dateStr) => {
		if (!dateStr) return language === "en" ? "Never" : "Mai";
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) return "-";
		const locale = language === "en" ? "en-US" : "it-IT";
		return date.toLocaleString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	// ── Coach Teams handlers ──
	const handleCreateTeam = async () => {
		if (!newTeam.name.trim()) {
			toast.error("Nome team richiesto");
			return;
		}
		try {
			await fetchAPI("/teams", {
				method: "POST",
				body: JSON.stringify(newTeam),
			});
			toast.success("Coach Team creato!");
			setIsTeamDialogOpen(false);
			setNewTeam({ name: "", description: "" });
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleDeleteTeam = async (teamId) => {
		if (!window.confirm(t("coachTeamsDeleteConfirm"))) return;
		try {
			await fetchAPI(`/teams/${teamId}`, { method: "DELETE" });
			toast.success(t("coachTeamsDeleted"));
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleAssignCoachToTeam = async (teamId, coachId) => {
		try {
			await fetchAPI(`/admin/teams/${teamId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "add" }),
			});
			toast.success("Coach assegnato al team");
			setTeamCoachSelections((prev) => ({ ...prev, [teamId]: "" }));
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleRemoveCoachFromTeam = async (teamId, coachId) => {
		try {
			await fetchAPI(`/admin/teams/${teamId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "remove" }),
			});
			toast.success("Coach rimosso dal team");
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	// ── Detail modal handlers ──
	const handleAssignAthleteToCoach = async (athleteId, coachId) => {
		try {
			await fetchAPI(`/admin/athletes/${athleteId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "add" }),
			});
			toast.success("Atleta assegnato");
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleRemoveAthleteFromCoach = async (athleteId, coachId) => {
		try {
			const res = await fetchAPI(`/admin/athletes/${athleteId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "remove" }),
			});
			// A no-op removal (athlete reached through a team or a shared group)
			// must not read as done: the athlete would still be in the list.
			if (res?.changed === false) {
				toast.error(t("adminCoachAthletesNotDirect"));
				reload();
				return;
			}
			toast.success("Atleta rimosso");
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleAddCoachToTeamModal = async (teamId, coachId) => {
		try {
			await fetchAPI(`/admin/teams/${teamId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "add" }),
			});
			toast.success("Coach aggiunto al team");
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleRemoveCoachFromTeamModal = async (teamId, coachId) => {
		try {
			await fetchAPI(`/admin/teams/${teamId}/assign-coach`, {
				method: "PUT",
				body: JSON.stringify({ coach_id: coachId, action: "remove" }),
			});
			toast.success("Coach rimosso dal team");
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleToggleCoachAiEnabled = async (coach) => {
		try {
			await fetchAPI(`/admin/users/${coach.user_id}`, {
				method: "PUT",
				body: JSON.stringify({ ai_enabled: !(coach.ai_enabled !== false) }),
			});
			toast.success(
				coach.ai_enabled !== false
					? "AI disattivata per il coach"
					: "AI riattivata per il coach",
			);
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	// Amministrazione dei dati di riferimento di una disciplina: division,
	// classi, tabelle punteggio e categorie di peso. Il modello prestativo di
	// sistema no: lo modifica solo l'admin pieno (D17).
	// **Non** sblocca utenti, team, programmi o impostazioni globali.
	const handleToggleReferenceAdmin = async (coach, sportValue) => {
		if (savingDisciplinesFor) return;
		const current = Array.isArray(coach.reference_admin_disciplines)
			? coach.reference_admin_disciplines
			: [];
		const next = current.includes(sportValue)
			? current.filter((v) => v !== sportValue)
			: [...current, sportValue];
		setSavingDisciplinesFor(coach.user_id);
		try {
			await fetchAPI(`/admin/users/${coach.user_id}`, {
				method: "PUT",
				body: JSON.stringify({ reference_admin_disciplines: next }),
			});
			toast.success(
				next.length === 0
					? "Nessuna disciplina da amministrare"
					: "Permessi di configurazione aggiornati",
			);
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setSavingDisciplinesFor(null);
		}
	};

	const handleToggleCoachDiscipline = async (coach, sportValue) => {
		if (savingDisciplinesFor) return;
		const current = Array.isArray(coach.disciplines) ? coach.disciplines : [];
		const next = current.includes(sportValue)
			? current.filter((v) => v !== sportValue)
			: [...current, sportValue];
		setSavingDisciplinesFor(coach.user_id);
		try {
			await fetchAPI(`/admin/users/${coach.user_id}`, {
				method: "PUT",
				body: JSON.stringify({ disciplines: next }),
			});
			toast.success(
				next.length === 0
					? "Nessuna disciplina: il coach vede tutte le sezioni"
					: "Discipline del coach aggiornate",
			);
			reload();
		} catch (error) {
			toast.error("Errore: " + error.message);
		} finally {
			setSavingDisciplinesFor(null);
		}
	};

	// Keep modal coach data fresh after reload
	const modalCoach = useMemo(() => {
		if (!detailModal) return null;
		return (
			coachDetails.find((c) => c.user_id === detailModal.coach.user_id) ||
			detailModal.coach
		);
	}, [detailModal, coachDetails]);

	// ── Filtered coaches ──
	const filteredCoaches = useMemo(() => {
		if (!searchQuery.trim()) return coachDetails;
		const q = searchQuery.toLowerCase();
		return coachDetails.filter(
			(c) =>
				c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q),
		);
	}, [coachDetails, searchQuery]);

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div className="space-y-8">
			{/* ═══════ COACH TEAMS SECTION ═══════ */}
			<Collapsible open={teamsOpen} onOpenChange={setTeamsOpen}>
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="p-6">
						<div className="flex items-center justify-between mb-4">
							<CollapsibleTrigger asChild>
								<button className="flex items-center gap-2 hover:text-volt transition-colors">
									<Building2 className="w-5 h-5 text-purple-400" />
									<h2 className="font-heading text-xl">Coach Team</h2>
									<Badge variant="outline" className="rounded-sm ml-2">
										{teams.length}
									</Badge>
									{teamsOpen ? (
										<ChevronUp className="w-4 h-4 text-muted-foreground" />
									) : (
										<ChevronDown className="w-4 h-4 text-muted-foreground" />
									)}
								</button>
							</CollapsibleTrigger>
							<Dialog
								open={isTeamDialogOpen}
								onOpenChange={setIsTeamDialogOpen}
							>
								<DialogTrigger asChild>
									<Button
										className="bg-volt hover:bg-volt/90 rounded-sm"
										size="sm"
									>
										<Plus className="w-4 h-4 mr-2" />
										Nuovo Team
									</Button>
								</DialogTrigger>
								<DialogContent className="bg-card border-border">
									<DialogDescription className="hidden" />
									<DialogHeader>
										<DialogTitle>Crea Coach Team</DialogTitle>
									</DialogHeader>
									<div className="space-y-4">
										<div className="space-y-2">
											<Label>Nome Coach Team</Label>
											<Input
												value={newTeam.name}
												onChange={(e) =>
													setNewTeam({ ...newTeam, name: e.target.value })
												}
												className="bg-secondary border-transparent rounded-sm"
												placeholder="Es: Coach Team Milano"
											/>
										</div>
										<div className="space-y-2">
											<Label>Descrizione</Label>
											<Input
												value={newTeam.description}
												onChange={(e) =>
													setNewTeam({
														...newTeam,
														description: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm"
												placeholder="Descrizione opzionale"
											/>
										</div>
										<Button
											onClick={handleCreateTeam}
											className="w-full bg-volt hover:bg-volt/90 rounded-sm"
										>
											Crea Coach Team
										</Button>
									</div>
								</DialogContent>
							</Dialog>
						</div>

						<CollapsibleContent>
							{teams.length > 0 ? (
								<div className="space-y-3">
									{teams.map((team) => {
										const detailedTeam = (coachAssignments.teams || []).find(
											(t) => t.team_id === team.team_id,
										);
										const teamCoaches = detailedTeam?.coaches || [];
										const teamAthletes = detailedTeam?.athletes || [];
										const shownAthletes = teamAthletes.slice(0, 12);
										const extraAthletes =
											teamAthletes.length - shownAthletes.length;
										const availableCoaches = allCoaches.filter(
											(c) =>
												!teamCoaches.some((tc) => tc.user_id === c.user_id),
										);
										const selectedCoachForTeam =
											teamCoachSelections[team.team_id] || "";
										return (
											<Card
												key={team.team_id}
												className="bg-secondary border-border rounded-sm"
											>
												<CardContent className="p-4">
													<div className="flex flex-col lg:flex-row lg:items-start gap-4">
														{/* Identita del team */}
														<div className="flex items-start gap-3 min-w-0 lg:w-60 shrink-0">
															<div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center shrink-0">
																<Building2 className="w-5 h-5 text-purple-400" />
															</div>
															<div className="min-w-0">
																<p className="font-heading text-lg leading-tight truncate">
																	{team.name}
																</p>
																{team.description && (
																	<p className="text-xs text-muted-foreground truncate">
																		{team.description}
																	</p>
																)}
																<div className="text-xs text-muted-foreground mt-1 break-words">
																	<span>{t("coachTeamsOwnerLabel")}: </span>
																	{team.owner_id ? (
																		<>
																			<span className="text-foreground">
																				{team.owner
																					? formatFullName(team.owner) ||
																						team.owner.email ||
																						team.owner_id
																					: t("coachTeamsOwnerUnavailable")}
																			</span>
																			{team.owner?.email && (
																				<span className="block break-all">
																					{team.owner.email}
																				</span>
																			)}
																		</>
																	) : (
																		t("coachTeamsNoOwner")
																	)}
																</div>
																<p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
																	<Users className="w-3.5 h-3.5 shrink-0" />
																	{teamCoaches.length} coach ·{" "}
																	{teamAthletes.length} atleti
																</p>
																<Button
																	variant="ghost"
																	size="icon"
																	className="mt-2 h-9 w-9 rounded-sm text-blaze hover:text-blaze hover:bg-blaze/10"
																	aria-label={`${t("delete")} ${team.name}`}
																	title={`${t("delete")} ${team.name}`}
																	onClick={() => handleDeleteTeam(team.team_id)}
																>
																	<Trash2 className="h-4 w-4" />
																</Button>
															</div>
														</div>

														{/* Coach del team + aggiunta */}
														<div className="flex-1 min-w-0 space-y-2">
															<p className="label-tactical">Coach del team</p>
															{teamCoaches.length > 0 ? (
																<div className="flex flex-wrap gap-1.5">
																	{teamCoaches.map((coach) => (
																		<div
																			key={coach.user_id}
																			className="flex items-center gap-1.5 max-w-full px-2 py-1 bg-card rounded-sm text-xs"
																			title={coach.email}
																		>
																			<span className="truncate">
																				{coach.name || coach.email}
																			</span>
																			<button
																				type="button"
																				aria-label={`Rimuovi ${coach.name} dal team`}
																				onClick={() =>
																					handleRemoveCoachFromTeam(
																						team.team_id,
																						coach.user_id,
																					)
																				}
																				className="shrink-0 text-muted-foreground hover:text-blaze"
																			>
																				<X className="w-3 h-3" />
																			</button>
																		</div>
																	))}
																</div>
															) : (
																<p className="text-xs text-muted-foreground">
																	Nessun coach assegnato
																</p>
															)}
															{availableCoaches.length > 0 ? (
																<div className="flex flex-wrap items-center gap-2 pt-1">
																	<Select
																		value={selectedCoachForTeam || undefined}
																		onValueChange={(v) =>
																			setTeamCoachSelections((prev) => ({
																				...prev,
																				[team.team_id]: v,
																			}))
																		}
																	>
																		{/* min-w-0 + larghezza esplicita: il trigger e
																		    w-full di default e spingeva "Aggiungi"
																		    fuori dalla card. */}
																		<SelectTrigger className="h-8 w-full sm:w-72 min-w-0 bg-card border-border rounded-sm">
																			<SelectValue placeholder="Seleziona coach da aggiungere" />
																		</SelectTrigger>
																		<SelectContent className="bg-card border-border">
																			{availableCoaches.map((coach) => (
																				<SelectItem
																					key={coach.user_id}
																					value={coach.user_id}
																				>
																					<span className="truncate">
																						{coach.name}
																					</span>
																					<span className="ml-1.5 text-[10px] text-muted-foreground">
																						{coach.email}
																					</span>
																				</SelectItem>
																			))}
																		</SelectContent>
																	</Select>
																	<Button
																		variant="outline"
																		size="sm"
																		className="h-8 rounded-sm shrink-0"
																		onClick={() => {
																			if (!selectedCoachForTeam) {
																				toast.error(
																					"Seleziona un coach da aggiungere",
																				);
																				return;
																			}
																			handleAssignCoachToTeam(
																				team.team_id,
																				selectedCoachForTeam,
																			);
																		}}
																	>
																		<UserPlus className="w-3.5 h-3.5 mr-1.5" />
																		Aggiungi
																	</Button>
																</div>
															) : (
																teamCoaches.length > 0 && (
																	<p className="text-xs text-muted-foreground">
																		Tutti i coach sono già presenti in questo
																		team
																	</p>
																)
															)}
														</div>

														{/* Atleti del team */}
														<div className="lg:w-72 shrink-0 space-y-2 lg:border-l lg:border-border/60 lg:pl-4">
															<p className="label-tactical">
																Atleti del team ({teamAthletes.length})
															</p>
															{teamAthletes.length > 0 ? (
																<div className="flex flex-wrap gap-1">
																	{shownAthletes.map((athlete) => (
																		<Badge
																			key={athlete.user_id}
																			variant="outline"
																			className="rounded-sm text-[10px] max-w-full truncate"
																			title={athlete.email}
																		>
																			{athlete.name}
																		</Badge>
																	))}
																	{extraAthletes > 0 && (
																		<span className="text-[10px] text-muted-foreground self-center">
																			+{extraAthletes} altri
																		</span>
																	)}
																</div>
															) : (
																<p className="text-xs text-muted-foreground">
																	Nessun atleta assegnato
																</p>
															)}
														</div>
													</div>
												</CardContent>
											</Card>
										);
									})}
								</div>
							) : (
								<div className="text-center py-8 text-muted-foreground">
									<Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
									<p>Nessun coach team creato</p>
								</div>
							)}
						</CollapsibleContent>
					</CardContent>
				</Card>
			</Collapsible>

			{/* ═══════ COACH LIST SECTION ═══════ */}
			{/* Righe a tutta larghezza, non card a 1/3 di schermo: le liste di
			    atleti e programmi in una colonna stretta erano illeggibili (e
			    andavano in scroll orizzontale). Il dettaglio sta solo nelle
			    modali, che sono gia pensate per quelle liste. */}
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-6">
					<div className="flex items-center justify-between mb-5">
						<div className="flex items-center gap-2">
							<UserCog className="w-5 h-5 text-acid" />
							<h2 className="font-heading text-xl">Coach</h2>
							<Badge variant="outline" className="rounded-sm ml-1">
								{coachDetails.length}
							</Badge>
						</div>
						<div className="relative w-64">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
							<Input
								placeholder="Cerca coach..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="pl-10 bg-secondary border-transparent rounded-sm"
							/>
						</div>
					</div>

					{filteredCoaches.length > 0 ? (
						<div className="space-y-3">
							{filteredCoaches.map((coach) => (
								<Card
									key={coach.user_id}
									className="bg-secondary border-border rounded-sm transition-colors hover:border-volt/20"
								>
									<CardContent className="p-4">
										<div className="flex flex-col xl:flex-row xl:items-center gap-4">
											{/* ── Identita ── */}
											<div className="flex items-start gap-3 min-w-0 xl:flex-1">
												<Avatar className="w-11 h-11 shrink-0">
													<AvatarImage src={coach.picture} />
													<AvatarFallback className="bg-acid text-background font-heading text-base">
														{coach.name?.charAt(0).toUpperCase()}
													</AvatarFallback>
												</Avatar>
												<div className="min-w-0">
													<div className="flex flex-wrap items-center gap-2">
														<p className="font-heading text-lg leading-tight truncate">
															{coach.name}
														</p>
														{coach.is_admin && (
															<Badge className="rounded-sm text-[10px] bg-acid/20 text-acid border-0">
																Admin
															</Badge>
														)}
														{coach.is_active === false && (
															<Badge
																variant="destructive"
																className="rounded-sm text-[10px]"
															>
																Inattivo
															</Badge>
														)}
														{coach.ai_enabled === false && (
															<Badge className="rounded-sm text-[10px] bg-blaze/15 text-blaze border-0">
																AI OFF
															</Badge>
														)}
													</div>
													<p className="text-xs text-muted-foreground truncate">
														{coach.email}
													</p>
													<div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-muted-foreground/80">
														<span className="flex items-center gap-1">
															<Clock className="w-3 h-3 shrink-0" />
															Ultimo accesso:{" "}
															{formatLastAccess(coach.last_access_at)}
														</span>
														{coach.created_at && (
															<span className="flex items-center gap-1">
																<Shield className="w-3 h-3 shrink-0" />
																Registrato:{" "}
																{new Date(coach.created_at).toLocaleDateString(
																	language === "it" ? "it-IT" : "en-US",
																)}
															</span>
														)}
														{/* Niente `coach.sport`: e' lo sport da atleta di chi si e'
														    registrato cosi', non quello che allena (B16 di
														    docs/sport-config). Gli sport seguiti sono sopra. */}
														{coach.recent_results_count > 0 && (
															<span className="flex items-center gap-1 text-green-400">
																<Activity className="w-3 h-3 shrink-0" />
																{coach.recent_results_count} risultati (30gg)
															</span>
														)}
													</div>
													{coach.teams?.length > 0 && (
														<div className="flex flex-wrap gap-1 mt-2">
															{coach.teams.map((t) => (
																<Badge
																	key={t.team_id}
																	className="bg-purple-500/20 text-purple-300 border-0 rounded-sm text-[10px]"
																>
																	<Building2 className="w-3 h-3 mr-1" />
																	{t.name} ({t.athlete_count})
																</Badge>
															))}
														</div>
													)}
												</div>
											</div>

											{/* ── Stats cliccabili: aprono le modali di dettaglio ── */}
											<div className="grid grid-cols-3 gap-2 shrink-0 xl:w-[270px]">
												<button
													onClick={() => {
														setDetailModal({ type: "athletes", coach });
														setModalSearch("");
														setAthletesTab("direct");
													}}
													className="bg-card rounded-sm px-2 py-2 text-center hover:ring-1 hover:ring-volt/40 transition-all cursor-pointer"
												>
													<p className="text-lg font-heading text-volt leading-tight">
														{coach.direct_athlete_count || 0}
													</p>
													<p className="text-[9px] leading-none text-muted-foreground uppercase tracking-wide">
														{t("adminCoachAthletesDirectStat")}
													</p>
													{coach.shared_athlete_count > 0 && (
														<p className="text-[9px] leading-tight text-purple-300 mt-1">
															{t("adminCoachAthletesSharedStat").replace(
																"{count}",
																coach.shared_athlete_count,
															)}
														</p>
													)}
												</button>
												<button
													onClick={() => {
														setDetailModal({ type: "programs", coach });
														setModalSearch("");
													}}
													className="bg-card rounded-sm px-2 py-2 text-center hover:ring-1 hover:ring-acid/40 transition-all cursor-pointer"
												>
													<p className="text-lg font-heading text-acid leading-tight">
														{coach.program_count}
													</p>
													<p className="text-[9px] leading-none text-muted-foreground uppercase tracking-wide">
														Programmi
													</p>
												</button>
												<button
													onClick={() => {
														setDetailModal({ type: "teams", coach });
														setModalSearch("");
													}}
													className="bg-card rounded-sm px-2 py-2 text-center hover:ring-1 hover:ring-purple-400/40 transition-all cursor-pointer"
												>
													<p className="text-lg font-heading text-purple-400 leading-tight">
														{coach.teams?.length || 0}
													</p>
													<p className="text-[9px] leading-none text-muted-foreground uppercase tracking-wide">
														Team
													</p>
												</button>
											</div>

											{/* ── Permessi ── */}
											{coach.is_admin ? (
												<div className="shrink-0 xl:w-72 text-[11px] text-muted-foreground italic">
													Admin: accesso completo a tutte le sezioni
												</div>
											) : (
												<div className="shrink-0 xl:w-72 rounded-sm border border-border/70 bg-card p-2.5">
													<div className="flex items-center justify-between gap-2 mb-2">
														<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
															Permessi
														</p>
														<Button
															type="button"
															size="sm"
															variant="ghost"
															className="h-6 px-2 rounded-sm text-[11px] text-muted-foreground hover:text-volt"
															onClick={() =>
																setDetailModal({ type: "permissions", coach })
															}
														>
															<Settings2 className="w-3.5 h-3.5 mr-1" />
															Gestisci
														</Button>
													</div>
													<div className="space-y-1">
														<div className="flex items-center gap-1.5 text-xs">
															<Bot
																className={`w-3.5 h-3.5 shrink-0 ${
																	coach.ai_enabled === false
																		? "text-blaze"
																		: "text-volt"
																}`}
															/>
															<span className="text-muted-foreground">
																{coach.ai_enabled === false
																	? "AI disattivata"
																	: "AI attiva"}
															</span>
														</div>
														{sports.length > 0 && (
															<>
																<DisciplineSummary
																	label="Segue"
																	values={coach.disciplines}
																	sports={sports}
																	emptyText="nessuno sport"
																	tone="volt"
																/>
																<DisciplineSummary
																	label="Configura"
																	values={coach.reference_admin_disciplines}
																	sports={sports}
																	emptyText="nessuna disciplina"
																	tone="blaze"
																/>
															</>
														)}
													</div>
												</div>
											)}
										</div>
									</CardContent>
								</Card>
							))}
						</div>
					) : (
						<div className="text-center py-12 text-muted-foreground">
							<UserCog className="w-12 h-12 mx-auto mb-4 opacity-50" />
							<p>
								{searchQuery
									? "Nessun coach trovato"
									: "Nessun coach registrato"}
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* ═══════ DETAIL MODAL ═══════ */}
			<Dialog
				open={!!detailModal}
				onOpenChange={(open) => {
					if (!open) setDetailModal(null);
				}}
			>
				<DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
					{detailModal?.type === "permissions" && modalCoach && (
						<>
							<DialogHeader>
								<DialogTitle className="font-heading">
									Permessi — {modalCoach.name}
								</DialogTitle>
								<DialogDescription>
									Funzioni AI, sezioni visibili e dati di riferimento
									configurabili.
								</DialogDescription>
							</DialogHeader>
							<div className="overflow-y-auto flex-1 space-y-5 pr-1 mt-2">
								{/* AI */}
								<div className="rounded-sm border border-border/70 bg-secondary p-3 flex items-center justify-between gap-3">
									<div className="min-w-0">
										<p className="label-tactical mb-1">AI Coach</p>
										<p className="text-xs text-muted-foreground">
											{modalCoach.ai_enabled === false
												? "Funzioni AI momentaneamente disattivate"
												: "Funzioni AI abilitate"}
										</p>
									</div>
									<Button
										type="button"
										size="sm"
										variant="outline"
										className={`rounded-sm shrink-0 ${
											modalCoach.ai_enabled === false
												? "border-dashed border-volt/50 text-volt"
												: "border-border"
										}`}
										onClick={() => handleToggleCoachAiEnabled(modalCoach)}
									>
										<Bot className="w-3.5 h-3.5 mr-1.5" />
										{modalCoach.ai_enabled === false
											? "Riabilita AI"
											: "Disabilita AI"}
									</Button>
								</div>

								{sports.length > 0 && (
									<>
										{/* Cosa cambia davvero per un coach: oggi conta l'ambito
										    (fisico / tiro), non il singolo sport. Stesso testo
										    che leggera' il coach nella sua sezione. */}
										<SportsExplainer audience="coach" />

										{/* Discipline seguite */}
										<div>
											<div className="flex items-center justify-between gap-3 mb-1">
												<p className="label-tactical">Discipline seguite</p>
												{savingDisciplinesFor === modalCoach.user_id && (
													<span className="text-[10px] text-muted-foreground">
														Salvataggio...
													</span>
												)}
											</div>
											<p className="text-xs text-muted-foreground mb-2">
												{/* D11 di docs/sport-config: il coach sceglie da se' i suoi
												    sport; quelli di tiro li assegna solo l'admin, da qui. */}
												{(modalCoach.disciplines || []).length === 0
													? "Nessuno sport: il coach lavora sulla preparazione fisica e non vede le sezioni di tiro"
													: "Il coach crea modelli suoi per questi sport. Quelli di tiro li assegni solo tu, e aprono le sezioni di tiro"}
											</p>
											<div className="grid grid-cols-2 gap-1.5">
												{sportsFor(modalCoach, modalCoach.disciplines).map((sport) => {
													const selected = (
														modalCoach.disciplines || []
													).includes(sport.value);
													return (
														<button
															key={sport.sport_id || sport.value}
															type="button"
															disabled={
																savingDisciplinesFor === modalCoach.user_id
															}
															onClick={() =>
																handleToggleCoachDiscipline(
																	modalCoach,
																	sport.value,
																)
															}
															className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 text-xs text-left transition-colors disabled:opacity-50 ${
																selected
																	? "border-volt/60 bg-volt/15 text-volt"
																	: "border-border text-muted-foreground hover:border-volt/40"
															}`}
														>
															<Check
																className={`w-3.5 h-3.5 shrink-0 ${
																	selected ? "opacity-100" : "opacity-20"
																}`}
															/>
															<span className="truncate">
																{sport.label || sport.value}
															</span>
														</button>
													);
												})}
											</div>
										</div>

										{/* Reference admin */}
										<div>
											<p className="label-tactical mb-1">Può configurare</p>
											<p className="text-xs text-muted-foreground mb-2">
												Division, classi, punteggi e profilo prestativo di
												queste discipline. Non utenti, team o programmi.
											</p>
											<div className="grid grid-cols-2 gap-1.5">
												{sportsFor(
													modalCoach,
													modalCoach.reference_admin_disciplines,
												).map((sport) => {
													const selected = (
														modalCoach.reference_admin_disciplines || []
													).includes(sport.value);
													return (
														<button
															key={`ra_${sport.value}`}
															type="button"
															disabled={
																savingDisciplinesFor === modalCoach.user_id
															}
															onClick={() =>
																handleToggleReferenceAdmin(
																	modalCoach,
																	sport.value,
																)
															}
															className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 text-xs text-left transition-colors disabled:opacity-50 ${
																selected
																	? "border-blaze/60 bg-blaze/15 text-blaze"
																	: "border-border text-muted-foreground hover:border-blaze/40"
															}`}
														>
															<Check
																className={`w-3.5 h-3.5 shrink-0 ${
																	selected ? "opacity-100" : "opacity-20"
																}`}
															/>
															<span className="truncate">
																{sport.label || sport.value}
															</span>
														</button>
													);
												})}
											</div>
										</div>
									</>
								)}
							</div>
						</>
					)}
					{detailModal?.type === "athletes" &&
						modalCoach &&
						(() => {
							// Each athlete comes with the reason it is in the coach's cone
							// (`relation`, from /admin/coach-details). Three tabs instead of
							// one long list: who is assigned by name, who arrives through a
							// team or a shared group (grouped by where they come from), and
							// the platform-wide picker, which only lists search results.
							const coachAthletes = modalCoach.athletes || [];
							const inConeIds = new Set(coachAthletes.map((a) => a.user_id));
							const matches = (a) => matchesCoachAthleteSearch(a, modalSearch);
							const hasSelf = coachAthletes.some((a) => a.relation === "self");
							const directRows = sortByName(
								coachAthletes.filter(
									(a) => a.relation === "direct" && matches(a),
								),
							);
							const sharedRows = coachAthletes.filter(
								(a) => a.relation === "shared" && matches(a),
							);
							const sharedBuckets = buildSharedAthleteBuckets(sharedRows, t);
							const searching = Boolean(modalSearch.trim());
							const addResults = searching
								? sortByName(
										allAthletes.filter(
											(a) => !inConeIds.has(a.user_id) && matches(a),
										),
									)
								: [];
							const shownAddResults = addResults.slice(0, ADD_RESULTS_LIMIT);
							return (
								<>
									<DialogHeader>
										<DialogTitle className="font-heading">
											Atleti — {modalCoach.name}
										</DialogTitle>
										<DialogDescription>
											{t("adminCoachAthletesSummary")
												.replace(
													"{direct}",
													modalCoach.direct_athlete_count || 0,
												)
												.replace(
													"{shared}",
													modalCoach.shared_athlete_count || 0,
												)}
										</DialogDescription>
										{hasSelf && (
											<p className="text-[11px] text-muted-foreground/80">
												{t("adminCoachAthletesSelfNote")}
											</p>
										)}
									</DialogHeader>
									<div className="relative">
										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
										<Input
											placeholder="Cerca atleta..."
											value={modalSearch}
											onChange={(e) => setModalSearch(e.target.value)}
											className="pl-10 bg-secondary border-transparent rounded-sm"
										/>
									</div>
									<Tabs
										value={athletesTab}
										onValueChange={setAthletesTab}
										className="flex-1 min-h-0 flex flex-col"
									>
										<TabsList className="rounded-sm w-full grid grid-cols-3">
											<TabsTrigger value="direct" className="rounded-sm">
												{t("adminCoachAthletesTabDirect")} ({directRows.length})
											</TabsTrigger>
											<TabsTrigger value="shared" className="rounded-sm">
												{t("adminCoachAthletesTabShared")} ({sharedRows.length})
											</TabsTrigger>
											<TabsTrigger value="add" className="rounded-sm">
												<UserPlus className="w-3.5 h-3.5 mr-1.5" />
												{t("adminCoachAthletesTabAdd")}
											</TabsTrigger>
										</TabsList>

										<TabsContent
											value="direct"
											className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1"
										>
											{directRows.length > 0 ? (
												directRows.map((a) => (
													<CoachAthleteRow
														key={a.user_id}
														athlete={a}
														action={
															<Button
																size="sm"
																variant="ghost"
																className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
																onClick={() =>
																	handleRemoveAthleteFromCoach(
																		a.user_id,
																		modalCoach.user_id,
																	)
																}
																title="Rimuovi"
															>
																<UserMinus className="w-3.5 h-3.5" />
															</Button>
														}
													/>
												))
											) : (
												<p className="text-center text-sm text-muted-foreground py-6">
													{searching
														? "Nessun atleta trovato"
														: t("adminCoachAthletesDirectEmpty")}
												</p>
											)}
										</TabsContent>

										<TabsContent
											value="shared"
											className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2"
										>
											{sharedBuckets.length > 0 && (
												<p className="text-[11px] text-muted-foreground">
													{t("adminCoachAthletesSharedHint")}
												</p>
											)}
											{sharedBuckets.map((bucket) => (
												<Collapsible
													key={bucket.key}
													defaultOpen={searching || sharedBuckets.length === 1}
													className="rounded-sm border border-border"
												>
													<CollapsibleTrigger className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/60">
														{bucket.kind === "team" ? (
															<Building2 className="w-4 h-4 text-purple-400 shrink-0" />
														) : (
															<Users className="w-4 h-4 text-purple-400 shrink-0" />
														)}
														<span className="min-w-0 flex-1">
															<span className="block text-sm font-medium truncate">
																{bucket.label}
															</span>
															{bucket.meta && (
																<span className="block text-[10px] text-muted-foreground truncate">
																	{bucket.meta}
																</span>
															)}
														</span>
														<Badge className="bg-purple-500/15 text-purple-300 border-0 rounded-sm text-[11px] shrink-0">
															{bucket.athletes.length}
														</Badge>
														<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180" />
													</CollapsibleTrigger>
													<CollapsibleContent className="space-y-1 px-2 pb-2">
														{bucket.athletes.map((a) => (
															<CoachAthleteRow
																key={a.user_id}
																athlete={a}
																muted
															/>
														))}
													</CollapsibleContent>
												</Collapsible>
											))}
											{sharedBuckets.length === 0 && (
												<p className="text-center text-sm text-muted-foreground py-6">
													{searching
														? "Nessun atleta trovato"
														: t("adminCoachAthletesSharedEmpty")}
												</p>
											)}
										</TabsContent>

										<TabsContent
											value="add"
											className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1"
										>
											{!searching ? (
												<p className="text-center text-sm text-muted-foreground py-6">
													{t("adminCoachAthletesAddHint")}
												</p>
											) : shownAddResults.length > 0 ? (
												<>
													{shownAddResults.map((a) => (
														<CoachAthleteRow
															key={a.user_id}
															athlete={a}
															muted
															action={
																<Button
																	size="sm"
																	variant="ghost"
																	className="h-7 w-7 p-0 text-volt hover:text-volt/80 hover:bg-volt/10 shrink-0"
																	onClick={() =>
																		handleAssignAthleteToCoach(
																			a.user_id,
																			modalCoach.user_id,
																		)
																	}
																	title="Assegna"
																>
																	<UserPlus className="w-3.5 h-3.5" />
																</Button>
															}
														/>
													))}
													{addResults.length > shownAddResults.length && (
														<p className="text-center text-[11px] text-muted-foreground py-2">
															{t("adminCoachAthletesAddMore").replace(
																"{count}",
																ADD_RESULTS_LIMIT,
															)}
														</p>
													)}
												</>
											) : (
												<p className="text-center text-sm text-muted-foreground py-6">
													Nessun atleta trovato
												</p>
											)}
										</TabsContent>
									</Tabs>
								</>
							);
						})()}

					{detailModal?.type === "programs" &&
						modalCoach &&
						(() => {
							const allProgs = modalCoach.programs || [];
							const ownPrograms = allProgs.filter((p) => p.is_owner);
							const sharedPrograms = allProgs.filter((p) => !p.is_owner);

							const ProgramRow = ({ p }) => (
								<div
									key={p.program_id}
									className="flex items-center justify-between p-3 bg-secondary rounded-sm hover:ring-1 hover:ring-volt/30 cursor-pointer transition-all group"
									onClick={() => navigate(`/programs/${p.program_id}`)}
								>
									<div className="min-w-0 flex-1">
										<p className="text-sm font-medium truncate group-hover:text-volt transition-colors">
											{p.name}
										</p>
										<div className="flex items-center gap-3 mt-1">
											<span className="text-[10px] text-muted-foreground flex items-center gap-1">
												<Users className="w-3 h-3" />
												{p.athlete_count} atleti
											</span>
											{p.updated_at && (
												<span className="text-[10px] text-muted-foreground flex items-center gap-1">
													<Clock className="w-3 h-3" />
													{new Date(p.updated_at).toLocaleDateString(
														language === "it" ? "it-IT" : "en-US",
													)}
												</span>
											)}
											{p.shared_by && (
												<span className="text-[10px] text-purple-400 flex items-center gap-1">
													<Share2 className="w-3 h-3" />
													da {p.shared_by}
													{p.shared_via === "team" &&
													p.shared_team_names?.length > 0
														? ` · via ${p.shared_team_names.join(", ")}`
														: p.shared_via === "team"
															? " · via team"
															: ""}
												</span>
											)}
										</div>
									</div>
									<ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-volt shrink-0 ml-2 transition-colors" />
								</div>
							);

							return (
								<>
									<DialogHeader>
										<DialogTitle className="font-heading">
											Programmi — {modalCoach.name}
										</DialogTitle>
										<DialogDescription>
											{ownPrograms.length} propri · {sharedPrograms.length}{" "}
											condivisi
										</DialogDescription>
									</DialogHeader>
									<div className="overflow-y-auto flex-1 space-y-4 pr-1">
										{ownPrograms.length > 0 && (
											<div>
												<p className="label-tactical mb-2">
													Propri ({ownPrograms.length})
												</p>
												<div className="space-y-2">
													{ownPrograms.map((p) => (
														<ProgramRow key={p.program_id} p={p} />
													))}
												</div>
											</div>
										)}
										{sharedPrograms.length > 0 && (
											<div>
												<p className="label-tactical mb-2">
													Condivisi ({sharedPrograms.length})
												</p>
												<div className="space-y-2">
													{sharedPrograms.map((p) => (
														<ProgramRow key={p.program_id} p={p} />
													))}
												</div>
											</div>
										)}
										{allProgs.length === 0 && (
											<p className="text-center text-sm text-muted-foreground py-6">
												Nessun programma
											</p>
										)}
									</div>
								</>
							);
						})()}

					{detailModal?.type === "teams" &&
						modalCoach &&
						(() => {
							const coachTeamIds = new Set(
								(modalCoach.teams || []).map((t) => t.team_id),
							);
							return (
								<>
									<DialogHeader>
										<DialogTitle className="font-heading">
											Team — {modalCoach.name}
										</DialogTitle>
										<DialogDescription>
											{modalCoach.teams?.length || 0} team assegnati
										</DialogDescription>
									</DialogHeader>
									<div className="overflow-y-auto flex-1 space-y-4 pr-1">
										{teams.length > 0 ? (
											<>
												{/* Teams the coach belongs to */}
												{teams.filter((t) => coachTeamIds.has(t.team_id))
													.length > 0 && (
													<div>
														<p className="label-tactical mb-2">Membro</p>
														<div className="space-y-1.5">
															{teams
																.filter((t) => coachTeamIds.has(t.team_id))
																.map((t) => {
																	const detail = (modalCoach.teams || []).find(
																		(x) => x.team_id === t.team_id,
																	);
																	return (
																		<div
																			key={t.team_id}
																			className="flex items-center gap-3 p-3 bg-secondary rounded-sm"
																		>
																			<Building2 className="w-5 h-5 text-purple-400 shrink-0" />
																			<div className="min-w-0 flex-1">
																				<p className="text-sm font-medium truncate">
																					{t.name}
																				</p>
																				{t.description && (
																					<p className="text-[10px] text-muted-foreground truncate">
																						{t.description}
																					</p>
																				)}
																				<span className="text-[10px] text-muted-foreground">
																					{detail?.athlete_count || 0} atleti
																				</span>
																			</div>
																			<Button
																				size="sm"
																				variant="ghost"
																				className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
																				onClick={() =>
																					handleRemoveCoachFromTeamModal(
																						t.team_id,
																						modalCoach.user_id,
																					)
																				}
																				title="Rimuovi dal team"
																			>
																				<X className="w-3.5 h-3.5" />
																			</Button>
																		</div>
																	);
																})}
														</div>
													</div>
												)}
												{/* Teams the coach does NOT belong to */}
												{teams.filter((t) => !coachTeamIds.has(t.team_id))
													.length > 0 && (
													<div>
														<p className="label-tactical mb-2">Non membro</p>
														<div className="space-y-1.5">
															{teams
																.filter((t) => !coachTeamIds.has(t.team_id))
																.map((t) => (
																	<div
																		key={t.team_id}
																		className="flex items-center gap-3 p-3 bg-secondary/50 rounded-sm"
																	>
																		<Building2 className="w-5 h-5 text-muted-foreground shrink-0" />
																		<div className="min-w-0 flex-1">
																			<p className="text-sm font-medium truncate">
																				{t.name}
																			</p>
																			{t.description && (
																				<p className="text-[10px] text-muted-foreground truncate">
																					{t.description}
																				</p>
																			)}
																		</div>
																		<Button
																			size="sm"
																			variant="ghost"
																			className="h-7 w-7 p-0 text-volt hover:text-volt/80 hover:bg-volt/10 shrink-0"
																			onClick={() =>
																				handleAddCoachToTeamModal(
																					t.team_id,
																					modalCoach.user_id,
																				)
																			}
																			title="Aggiungi al team"
																		>
																			<Plus className="w-3.5 h-3.5" />
																		</Button>
																	</div>
																))}
														</div>
													</div>
												)}
											</>
										) : (
											<p className="text-center text-sm text-muted-foreground py-6">
												Nessun team creato
											</p>
										)}
									</div>
								</>
							);
						})()}
				</DialogContent>
			</Dialog>
		</div>
	);
}
